import { useEffect, useRef, useState } from "react";
import { Megaphone, Copy, Loader2, Sparkles, Image as ImageIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { rotationPreview, generateMissingTagPosts, generateTagPosts, sendRotationPosts, voiceCardUrl, patchCandidate } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { PostHook, PostHistory, isStale, STALE_DAYS } from "./PostMeta";
import { TrendingHooks } from "./TrendingHooks";

const intent = (net, text) =>
  net === "x"
    ? `https://x.com/intent/post?text=${encodeURIComponent(text)}`
    : `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}`;

const PostLine = ({ net, text }) => (
  <div className="flex items-start gap-3 py-2 border-t border-slate-100">
    <span className="w-14 shrink-0 text-[10px] uppercase tracking-wider text-slate-500 pt-1">{net === "x" ? "X" : "Bluesky"}</span>
    <p className="flex-1 text-sm text-slate-700">{text}</p>
    <div className="flex shrink-0 gap-2 pt-0.5">
      <button type="button" data-testid={`posts-copy-${net}`} onClick={() => copyText(text).then(() => toast.success("Copied"))} className="text-xs text-slate-500 hover:text-[#0A192F] inline-flex items-center gap-1">
        <Copy className="w-3 h-3" /> Copy
      </button>
      <a href={intent(net, text)} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--accent)] hover:text-[#0A192F]">Open →</a>
    </div>
  </div>
);

const CardPicker = ({ v, candidate, onChange }) => {
  const style = candidate?.card_style || "dark";
  const pick = async (s) => {
    try {
      onChange(await patchCandidate(v.id, { card_style: s }));
    } catch (err) {
      toast.error(extractApiError(err, "Could not save style."));
    }
  };
  const url = voiceCardUrl(v.id, style) + `&v=${encodeURIComponent(candidate?.updated_at || "")}`;
  return (
    <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-slate-100" data-testid="posts-card">
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="posts-card-link" className="shrink-0">
        <img src={url} alt={`Share card for ${v.name}`} loading="lazy" className="w-40 border border-slate-200" />
      </a>
      <div className="space-y-2">
        <div className="flex gap-1" data-testid="posts-card-style">
          {["dark", "light"].map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`posts-card-style-${s}`}
              aria-pressed={style === s}
              onClick={() => pick(s)}
              className={`px-3 py-1 text-[10px] uppercase tracking-wider border transition-colors ${
                style === s ? "bg-[#0A192F] border-[#0A192F] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <a href={url} download={`ATFQ-${v.name.replace(/\s+/g, "-")}-${style}.png`} className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:text-[#0A192F]">
          <ImageIcon className="w-3.5 h-3.5" /> Download {style} card
        </a>
      </div>
    </div>
  );
};

const VoiceRow = ({ v, candidate, onChange }) => {
  const [busy, setBusy] = useState(false);
  const posts = candidate?.tag_posts;
  const gen = async () => {
    setBusy(true);
    try {
      const { tag_posts } = await generateTagPosts(v.id);
      const history = posts?.x ? [...(candidate?.post_history || []), posts] : candidate?.post_history;
      onChange({ ...candidate, tag_posts, post_history: history });
      toast.success("Fresh posts written");
    } catch (err) {
      toast.error(extractApiError(err, "Could not write posts."));
    } finally {
      setBusy(false);
    }
  };
  const stale = isStale(posts);
  return (
    <div data-testid={`posts-voice-${v.id}`} className="border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="text-xs text-[var(--accent)] mr-2">{v.pillar}</span>
          <span className="font-display text-lg text-[#0A192F]">{v.name}</span>
          <span className="text-sm text-slate-500"> · {v.role}</span>
        </div>
        <button
          type="button"
          data-testid={posts?.x ? "posts-rewrite-one" : "posts-generate-one"}
          onClick={gen}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 text-xs disabled:opacity-50 ${stale && posts?.x ? "text-amber-600 hover:text-[#0A192F]" : "text-[var(--accent)] hover:text-[#0A192F]"}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {busy ? "Searching the news…" : posts?.x ? "Rewrite with fresh angle" : "Write posts"}
        </button>
      </div>
      {posts?.x ? (
        <div className="mt-2">
          <div className="mb-2"><PostHook posts={posts} /></div>
          <PostLine net="x" text={posts.x} />
          <PostLine net="bluesky" text={posts.bluesky} />
          <PostHistory history={candidate?.post_history} />
          <CardPicker v={v} candidate={candidate} onChange={onChange} />
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-400">No posts written yet.</p>
      )}
    </div>
  );
};

export const PostsSheet = ({ candidates, onChange, onRefresh }) => {
  const [data, setData] = useState(null);
  const [queued, setQueued] = useState([]);
  const [mailing, setMailing] = useState(false);
  const byId = Object.fromEntries(candidates.map((c) => [c.id, c]));

  const mailToday = async () => {
    setMailing(true);
    try {
      const res = await sendRotationPosts();
      toast.success(`Writing fresh posts for ${res.queued} voices and emailing ${res.day}'s rotation — arrives in a few minutes.`);
    } catch (err) {
      toast.error(extractApiError(err, "Could not send."));
    } finally {
      setMailing(false);
    }
  };

  useEffect(() => {
    rotationPreview().then(setData).catch(() => setData({ rotations: [], featured_count: 0 }));
  }, [candidates.length]);

  const stillStale = queued.filter((id) => !byId[id] || isStale(byId[id].tag_posts));
  const polls = useRef(0);
  useEffect(() => {
    if (!queued.length) return undefined;
    if (!stillStale.length || polls.current >= 30) {
      if (stillStale.length) toast.error(`${stillStale.length} voice(s) couldn't be written — check backend logs`);
      else toast.success(`Fresh posts written for ${queued.length} voices`);
      polls.current = 0;
      setQueued([]);
      return undefined;
    }
    const t = setTimeout(() => { polls.current += 1; onRefresh(); }, 10000);
    return () => clearTimeout(t);
  }, [queued, stillStale.length, onRefresh]);

  const missing = candidates.filter((c) => c.featured && isStale(c.tag_posts)).length;
  const busy = queued.length > 0;

  const genAll = async () => {
    try {
      const res = await generateMissingTagPosts();
      if (!res.queued?.length) return toast.info("Nothing to write — every featured voice has fresh posts.");
      toast.success(`Searching the news and writing posts for ${res.queued.length} voices…`);
      setQueued(res.queued);
    } catch (err) {
      toast.error(extractApiError(err, "Generation failed."));
    }
  };

  if (!data) return null;

  return (
    <section data-testid="scout-posts">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="text-xs tracking-tight text-[var(--accent)] flex items-center gap-2">
          <Megaphone className="w-3.5 h-3.5" /> This Week&apos;s Posts · {data.featured_count} featured voices
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-testid="posts-email-today" onClick={mailToday} disabled={mailing} className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0A192F] disabled:opacity-50 px-4 py-2 text-xs font-medium transition-colors">
            {mailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {mailing ? "Sending…" : "Email today's posts"}
          </button>
          {missing > 0 && (
            <button type="button" data-testid="posts-generate-all" onClick={genAll} disabled={busy} className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[var(--accent)] disabled:opacity-50 text-white px-4 py-2 text-xs font-medium transition-colors">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? `Writing… ${queued.length - stillStale.length}/${queued.length}` : `Write ${missing} missing/stale`}
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-3 mb-6">
        Auto-emailed every Mon/Wed/Fri at 7:30 AM ET. Posts are anchored to that week&apos;s news and rewritten with a new angle once they&apos;re {STALE_DAYS}+ days old — no repeats.
      </p>
      <TrendingHooks refreshKey={candidates.map((c) => c.tag_posts?.written_at || "").join("|")} />
      {data.featured_count === 0 ? (
        <p data-testid="scout-posts-empty" className="text-sm text-slate-500 border-t border-slate-200 pt-8">
          Feature a few voices first — this sheet lines up posts for each Mon/Wed/Fri rotation.
        </p>
      ) : (
        <div className="space-y-10">
          {data.rotations.map((r) => (
            <div key={r.date} data-testid={`posts-rotation-${r.date}`}>
              <div className="font-display text-2xl text-[#0A192F] tracking-tight mb-1">{r.label}</div>
              <div className="text-xs text-slate-500 mb-4">{r.date} · post the morning the rotation goes live</div>
              <div className="space-y-3">
                {r.voices.map((v) => <VoiceRow key={`${r.date}-${v.id}`} v={v} candidate={byId[v.id]} onChange={onChange} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PostsSheet;
